import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Typography,
  FormControl,
  Select,
  Slider,
  Button,
  Paper,
  InputLabel,
  MenuItem,
  Rating,
  Tooltip,
  useMediaQuery
} from '@mui/material';

import {
  Psychology,
  Circle,
  CircleOutlined,
  Bolt,
  AttachMoney,
  AutoFixHigh,
  Search,
  Event,
  Input,
  Window,
  TextSnippet,
  Image,
  PictureAsPdf,
  Output,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { getTranslucentColor } from '../../themes';

import {
  ENGINE_TEMPERATURE_DEFAULTS,
  ENGINE_TEMPERATURE_RANGES,
  MODEL_METADATA,
  AVAILABLE_MODELS,
  DEFAULT_ENGINE_MODELS,
} from '../../constants/appConstants';

const EngineSettings = ({ initialSettings, onSave }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedEngine, setSelectedEngine] = useState('chatgpt');
  const [engineSettings, setEngineSettings] = useState({});

  useEffect(() => {
    if (initialSettings) {
      setEngineSettings(initialSettings.engineSettings || {});

      // Set initial selected engine
      if (initialSettings.selectedEngine && initialSettings.selectedEngine !== 'guru') {
        setSelectedEngine(initialSettings.selectedEngine);
      }
    }
  }, []);

  // Only update engineSettings when initialSettings change, without changing selected engine
  useEffect(() => {
    if (initialSettings) {
      setEngineSettings(initialSettings.engineSettings || {});
    }
  }, [initialSettings]);

  const engineOptions = [
    { key: 'chatgpt', text: t('engine.chatgpt') },
    { key: 'claude', text: t('engine.claude') },
    { key: 'gemini', text: t('engine.gemini') },
    { key: 'deepseek', text: t('engine.deepseek') },
    { key: 'grok', text: t('engine.grok') },
  ];

  const handleEngineSelect = (event) => {
    const newEngine = event.target.value;
    setSelectedEngine(newEngine);

    setEngineSettings(prev => {
      const updated = { ...prev };
      if (!updated[newEngine] || !updated[newEngine].model) {
        updated[newEngine] = {
          ...(updated[newEngine] || {}),
          model: DEFAULT_ENGINE_MODELS[newEngine],
          temperature: ENGINE_TEMPERATURE_DEFAULTS[newEngine]
        };
      }
      return updated;
    });
  };

  const handleTemperatureChange = (engine, value) => {
    setEngineSettings(prev => {
      const updated = {
        ...prev,
        [engine]: {
          ...(prev[engine] || {}),
          temperature: value
        }
      };
      // Save changes automatically
      onSave({ engineSettings: updated });
      return updated;
    });
  };

  const handleModelChange = (event) => {
    const newModel = event.target.value;

    setEngineSettings(prev => {
      const updated = {
        ...prev,
        [selectedEngine]: {
          ...(prev[selectedEngine] || {}),
          model: newModel
        }
      };
      // Save changes automatically
      onSave({ engineSettings: updated });
      return updated;
    });
  };

  const handleRestoreDefault = () => {
    const defaultSettings = {
      ...engineSettings,
      [selectedEngine]: {
        temperature: ENGINE_TEMPERATURE_DEFAULTS[selectedEngine],
        model: DEFAULT_ENGINE_MODELS[selectedEngine]
      }
    };
    setEngineSettings(defaultSettings);
    // Save changes automatically
    onSave({ engineSettings: defaultSettings });
  };

  const inputTypeIcons = {
    text: <TextSnippet fontSize="small" />,
    image: <Image fontSize="small" />,
    pdf: <PictureAsPdf fontSize="small" />
  };

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%', // Prevent content from exceeding container width
    }}>
      <Typography variant="h6" gutterBottom>
        {t('settings.engineSettings.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('settings.engineSettings.description')}
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel id="engine-select-label">
          {t('settings.engineSettings.selectEngine')}
        </InputLabel>
        <Select
          labelId="engine-select-label"
          id="engine-select"
          value={selectedEngine}
          onChange={handleEngineSelect}
          label={t('settings.engineSettings.selectEngine')}
        >
          {engineOptions.filter(e => e.key !== 'guru').map(engine => (
            <MenuItem key={engine.key} value={engine.key}>
              {engine.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Model selection */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('settings.engineSettings.modelConfiguration')}
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="model-select-label">
            {t('settings.engineSettings.modelSelection')}
          </InputLabel>
          <Select
            labelId="model-select-label"
            id="model-select"
            value={engineSettings[selectedEngine]?.model || DEFAULT_ENGINE_MODELS[selectedEngine]}
            onChange={(e) => handleModelChange(e)}
            label={t('settings.engineSettings.modelSelection')}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 400 }
              }
            }}
          >
            {AVAILABLE_MODELS[selectedEngine]?.map(model => (
              <MenuItem key={model.id} value={model.id}>
                {model.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Model information card */}
        {selectedEngine && engineSettings[selectedEngine]?.model && MODEL_METADATA[engineSettings[selectedEngine]?.model] && (
          <Paper
            variant="outlined"
            sx={(theme) => ({
              mt: 2,
              p: 2,
              backgroundColor: getTranslucentColor(theme, 'xs'),
              borderRadius: 1
            })}
          >
            <Typography variant="subtitle2" gutterBottom>
              {AVAILABLE_MODELS[selectedEngine]?.find(m => m.id === engineSettings[selectedEngine]?.model)?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.description}
            </Typography>
            {/* Table layout for model specs */}
            <Box sx={{
              width: '100%',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              {/* Row 1: Intelligence, Speed, Pricing */}
              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                {/* Intelligence */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Psychology fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    {t('settings.engineSettings.intelligence')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Rating
                      name="intelligence-rating"
                      value={MODEL_METADATA[engineSettings[selectedEngine]?.model]?.intelligence || 5}
                      max={10}
                      readOnly
                      size="small"
                      precision={0.5}
                      icon={<Circle fontSize="inherit" />}
                      emptyIcon={<CircleOutlined fontSize="inherit" />}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                {/* Speed */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Bolt fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    {t('settings.engineSettings.speed')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Rating
                      name="speed-rating"
                      value={MODEL_METADATA[engineSettings[selectedEngine]?.model]?.speed}
                      max={10}
                      readOnly
                      size="small"
                      precision={0.5}
                      icon={<Circle fontSize="inherit" />}
                      emptyIcon={<CircleOutlined fontSize="inherit" />}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                {/* Cost */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                }}>
                  <AttachMoney fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    {t('settings.engineSettings.pricing')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Rating
                      name="pricing-rating"
                      value={MODEL_METADATA[engineSettings[selectedEngine]?.model]?.pricing}
                      max={10}
                      readOnly
                      size="small"
                      precision={0.5}
                      icon={<Circle fontSize="inherit" />}
                      emptyIcon={<CircleOutlined fontSize="inherit" />}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Row 2: Features (Reasoning, Web Search, Input Types) */}
              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                {/* Reasoning */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <AutoFixHigh fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.reasoning')}:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 'auto', fontWeight: 'medium', color: MODEL_METADATA[engineSettings[selectedEngine]?.model]?.reasoning ? 'success.main' : 'text.secondary' }}>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.reasoning ? t('yes') : t('no')}
                  </Typography>
                </Box>
                {/* Web Search */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Search fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.webSearch')}:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 'auto', fontWeight: 'medium', color: MODEL_METADATA[engineSettings[selectedEngine]?.model]?.webSearch ? 'success.main' : 'text.secondary' }}>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.webSearch ? t('yes') : t('no')}
                  </Typography>
                </Box>
                {/* Input Types */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                }}>
                  <Input fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.inputTypes')}:
                  </Typography>
                  <Box sx={{ display: 'flex', ml: 'auto', gap: 0.5 }}>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.inputTypes.map(type => (
                      <Tooltip key={type} title={type.charAt(0).toUpperCase() + type.slice(1)}>
                        {inputTypeIcons[type]}
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Box>
              {/* Row 3: Context window, Max Output, and Knowledge Cutoff */}
              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                {/* Context window */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Window fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.contextWindow')}:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 'auto' }}>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.contextWindow.toLocaleString()}
                  </Typography>
                </Box>
                {/* Max output */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1,
                  borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Output fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.maxOutput')}:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 'auto' }}>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.outputTokens.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isMobile ? '100%' : '33%',
                  p: 1
                }}>
                  <Event fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span" noWrap>
                    {t('settings.engineSettings.knowledgeCutoff')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Typography variant="body2" component="span">
                      {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.knowledgeCutoff}
                    </Typography>
                    {MODEL_METADATA[engineSettings[selectedEngine]?.model]?.webSearch && (
                      <Tooltip title={t('settings.engineSettings.webSearchCapable')}>
                        <Search fontSize="small" sx={{ ml: 0.5, color: 'success.main' }} />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('settings.engineSettings.temperatureLabel')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.engineSettings.temperatureDescription')}
        </Typography>
        <Box sx={{
          width: '100%',
          mt: 2,
          px: 1,
          '& .MuiSlider-rail': { width: '100%' },
          '& .MuiSlider-track': { width: '100%' }
        }}>
          <Typography id="temperature-slider" gutterBottom>
            {t('settings.engineSettings.temperature')}: {
              typeof engineSettings[selectedEngine]?.temperature === 'number'
                ? engineSettings[selectedEngine].temperature.toFixed(2)
                : '0.00'
            }
          </Typography>
          <Slider
            value={
              typeof engineSettings[selectedEngine]?.temperature === 'number'
                ? engineSettings[selectedEngine].temperature
                : ENGINE_TEMPERATURE_DEFAULTS[selectedEngine]
            }
            min={ENGINE_TEMPERATURE_RANGES[selectedEngine]?.min || 0}
            max={ENGINE_TEMPERATURE_RANGES[selectedEngine]?.max || 2}
            step={0.01}
            onChange={(_, value) => handleTemperatureChange(selectedEngine, value)}
            aria-labelledby="temperature-slider"
            sx={{
              '& .MuiSlider-thumb': {
                transition: 'none', // Disable animations that might cause position shifting
              }
            }}
          />
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mt: 1,
            fontSize: '0.75rem',
            color: 'text.secondary'
          }}>
            <Typography variant="caption">{t('settings.engineSettings.precise')}</Typography>
            <Typography variant="caption">{t('settings.engineSettings.creative')}</Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 4, mb: 4, textAlign: 'right' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRestoreDefault}
            sx={{ mr: 2 }}
          >
            {t('settings.engineSettings.restoreDefault')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EngineSettings;